/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { Button, Grid, IconButton, InlineNotification, Typography, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { AddIcon, CurriculumIcon } from '@coursera/cds-icons';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import AdditionalInfoSkeletonLoader from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/AdditionalInfoSkeletonLoader';
import EditAdditionalInfoDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/EditAdditionalInfoDialog';
import ExternalLinks from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/ExternalLinks';
import ExternalLinksItem from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/ExternalLinksItem';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  customCss?: Interpolation<Theme>;
};

type Props = PropsFromCaller;

const styles = {
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  aboutText: css`
    margin-top: var(--cds-spacing-300);
  `,
  emptyStateText: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  sectionTitleContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  editIcon: css`
    margin: calc(var(--cds-spacing-50)} * -1) calc(var(--cds-spacing-200) * -1) calc(var(--cds-spacing-50) * -1) 0;
    width: auto;
    align-self: flex-start;
  `,
};

const AdditionalInfoSection = ({ customCss }: Props) => {
  const { profileState, isProfileLoading, editable, restrictHiringSolutions } = useContext(ProfileContext);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const hasExternalLinks =
    profileState?.externalLinks &&
    Object.values(profileState?.externalLinks).filter(
      // When we get all the values from the links, it also includes the typename: LearnerProfile_ExternalLinks. We need to ignore it or else
      // we'll get false positives.
      (value) => Boolean(value) && value !== 'LearnerProfile_ExternalLinks'
    ).length > 0;
  const hasNoContent = !profileState?.about && !profileState?.resumeUrl && !hasExternalLinks;
  const showEmptyState = editable && hasNoContent;

  if (hasNoContent && !editable) return null;

  if (isProfileLoading) {
    return (
      <ProfileViewerCard customCss={customCss}>
        <AdditionalInfoSkeletonLoader />
      </ProfileViewerCard>
    );
  }

  const getEmptyStateBodyText = () => {
    return restrictHiringSolutions
      ? _t('Help viewers of your profile get to know you better by adding links and a short summary about yourself.')
      : _t(
          'Help recruiters get to know you better by describing what makes you a great candidate and sharing other links.'
        );
  };

  const getLinksHeader = () => {
    return restrictHiringSolutions ? _t('Links') : _t('Links & Resume');
  };

  if (showEmptyState) {
    return (
      <ProfileViewerCard customCss={customCss}>
        <Typography variant="h2semibold" data-testid="additional-info-section-title" css={styles.sectionTitle}>
          {_t('Additional info')}
        </Typography>
        <Typography2 component="p" color="supportText" css={styles.emptyStateText}>
          {getEmptyStateBodyText()}
        </Typography2>
        <Button
          onClick={() => setShowEditDialog(true)}
          icon={<AddIcon />}
          iconPosition="before"
          variant="secondary"
          size="small"
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_add_additional_info"
          requireFullyVisible={false}
          withVisibilityTracking={true}
        >
          {_t('Add additional info')}
        </Button>
        <EditAdditionalInfoDialog isOpen={showEditDialog} onClose={() => setShowEditDialog(false)} />
      </ProfileViewerCard>
    );
  }

  return (
    <ProfileViewerCard customCss={customCss}>
      <div css={styles.sectionTitleContainer}>
        <Typography variant="h2semibold" data-testid="additional-info-section-title" css={styles.sectionTitle}>
          {_t('Additional info')}
        </Typography>

        {editable && (
          <>
            <Grid container direction="row" justifyContent="flex-end" css={styles.editIcon}>
              <IconButton
                size="small"
                onClick={() => setShowEditDialog(true)}
                intent="edit"
                aria-label={_t('Edit additional information.')}
                variant="ghost"
                component={TrackedButton}
                // not tracked in eventing v3
                trackingName="profile_edit_additional_info"
                requireFullyVisible={false}
                withVisibilityTracking={true}
              />
            </Grid>
            <EditAdditionalInfoDialog isOpen={showEditDialog} onClose={() => setShowEditDialog(false)} />
          </>
        )}
      </div>
      {(hasExternalLinks || profileState?.resumeUrl) && (
        <Typography2 component="h3" css={styles.sectionTitle} variant="subtitleMedium">
          {getLinksHeader()}
        </Typography2>
      )}
      {hasExternalLinks && <ExternalLinks links={profileState?.externalLinks || {}} />}
      {profileState?.preSignedResumeUrl && (
        <ExternalLinksItem icon={<CurriculumIcon />} url={profileState?.preSignedResumeUrl} text={_t('Resume')} />
      )}
      <Typography2 component="p" css={styles.aboutText}>
        {profileState?.about}
      </Typography2>
    </ProfileViewerCard>
  );
};

const ErrorBoundAdditionalInfoSection = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="AdditionalInfoSection"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t(
          'There has been an error in the additional info section of the page. Our development team will be alerted.'
        )}
      </InlineNotification>
    }
  >
    <AdditionalInfoSection {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundAdditionalInfoSection;
